// Helper central que substitui as antigas chamadas fetch('/api/<name>') por
// get/set no Firestore, preservando o shape dos blobs.
//
// Histórico rápido:
//  - Antes: cada feature tinha um endpoint Pages Functions (/api/timeline,
//    /api/hotels, etc) que lia/gravava um blob JSON único em KV.
//  - Migração KV→Firestore (scripts/migrate-kv-to-firestore.mjs) moveu cada
//    blob para `trips/{t}/<subColl>/_legacy` (ou `trips/{t}/meta/<name>`
//    para os docs de meta), preservando o shape exato.
//  - Esse módulo traduz o nome antigo da API ("timeline", "balance", etc)
//    pro path Firestore correspondente. A chamada de alto nível continua
//    igual — o código de cada feature só troca `fetch('/api/foo')` por
//    `cloudLoad(tripId, 'foo')`.
//
// Shape preservado → a lógica de conflito baseada em `_ts` continua
// funcionando do jeito que já estava. Quando alguma feature evoluir para
// usar documentos individuais (em vez do blob _legacy), ela faz isso
// localmente sem afetar as outras.

import { getSubDoc, setSubDoc, getMeta, setMeta } from './firestore.js';

// Mapa dos nomes antigos (como vinham em /api/<name>) para o local no Firestore.
// Cada entrada: { type: 'blob'|'meta', subColl?: string, metaName?: string }
const RESOURCES = {
  timeline:            { type: 'blob', subColl: 'timeline' },
  itinerary:           { type: 'blob', subColl: 'itinerary' },
  hotels:              { type: 'blob', subColl: 'hotels' },
  routes:              { type: 'blob', subColl: 'routes' },
  expenses:            { type: 'blob', subColl: 'expenses' },
  'future-receivables': { type: 'blob', subColl: 'futureReceivables' },
  documents:           { type: 'blob', subColl: 'documents' },
  balance:             { type: 'meta', metaName: 'balance' },
  'balance/current':   { type: 'meta', metaName: 'currentBalance' },
  currency:            { type: 'meta', metaName: 'currency' },
  // Settings da viagem (cotações compartilhadas + dia de fechamento da
  // fatura). Doc: trips/{id}/meta/settings. Sem isto, saveTripSettings
  // lançava "recurso desconhecido settings".
  settings:            { type: 'meta', metaName: 'settings' },
};

/**
 * Lê o blob de um recurso. Retorna sempre um objeto — `{}` se não existir,
 * pra manter paridade com a API antiga que devolvia `{}` quando a key não
 * existia no KV.
 *
 * Strip de campos internos (`id`, `_legacy`, `_migratedAt`, `updatedAt`)
 * que foram adicionados pela camada Firestore/migração. O código da
 * feature só enxerga o payload de dados puro (same shape do KV antigo).
 */
export async function cloudLoad(tripId, name) {
  if (!tripId) throw new Error('cloudLoad: tripId obrigatório');
  const res = RESOURCES[name];
  if (!res) throw new Error('cloudLoad: recurso desconhecido "' + name + '"');

  const doc = res.type === 'blob'
    ? await getSubDoc(tripId, res.subColl, '_legacy')
    : await getMeta(tripId, res.metaName);

  if (!doc) return {};
  const { id, _legacy, _migratedAt, updatedAt, ...clean } = doc;
  return clean;
}

/**
 * Grava o blob de um recurso. Preserva o shape da app; adiciona só `_legacy: true`
 * nos sub-blobs para marcar que ainda é o doc consolidado (vs docs individuais
 * que alguma feature possa criar no futuro).
 */
export async function cloudSave(tripId, name, payload) {
  if (!tripId) throw new Error('cloudSave: tripId obrigatório');
  const res = RESOURCES[name];
  if (!res) throw new Error('cloudSave: recurso desconhecido "' + name + '"');

  // merge: false = replace. O payload representa o ESTADO COMPLETO do blob
  // (a feature remonta overrides/listas inteiras). Sem isso, campos removidos
  // na UI persistiriam fantasmagoricamente no Firestore.
  if (res.type === 'blob') {
    await setSubDoc(tripId, res.subColl, '_legacy', { ...payload, _legacy: true }, { merge: false });
  } else {
    await setMeta(tripId, res.metaName, payload, { merge: false });
  }
}
